import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Ship, BookOpen } from "lucide-react";

const CATEGORIES = ["Ship", "Cafe", "Retail shop", "Hotel", "Office", "Individual", "Other"];
const EMPTY = { name: "", category: "Ship", contact_person: "", phone: "", email: "", address: "", gstin: "", payment_terms: "", notes: "", active: true };

export default function BuyersTab({ onViewLedger }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/buyers");
      setItems(data);
    } catch { toast.error("Could not load buyers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (v) => { setEditing(v); setForm({ ...EMPTY, ...v }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Buyer name is required"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        const { data } = await api.post("/staff/buyers", form);
        setItems((p) => [...p, data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`Added ${data.name}`);
      } else {
        const { data } = await api.patch(`/staff/buyers/${editing.id}`, form);
        setItems((p) => p.map((v) => (v.id === data.id ? data : v)));
        toast.success(`Updated ${data.name}`);
      }
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Deactivate buyer "${v.name}"? Past dispatches remain intact.`)) return;
    try {
      await api.delete(`/staff/buyers/${v.id}`);
      setItems((p) => p.map((x) => (x.id === v.id ? { ...x, active: false } : x)));
      toast.success("Deactivated");
    } catch { toast.error("Delete failed"); }
  };

  const filtered = showInactive ? items : items.filter((v) => v.active);

  return (
    <div data-testid="buyers-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm">
          <input data-testid="buyers-show-inactive" type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <button data-testid="buyer-new" onClick={openNew} className="btn-brass !py-2 !px-4 text-sm">
          <Plus size={14} /> New buyer
        </button>
      </div>

      <div className="mt-6 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
        {loading && <div className="p-6 text-[hsl(var(--ink-soft))]">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div data-testid="buyers-empty" className="p-10 text-center text-[hsl(var(--ink-soft))]">
            <Ship className="mx-auto mb-2 opacity-40" size={28} />
            No buyers yet — add your first one (shop, ship, café).
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {filtered.map((v) => (
              <li key={v.id} data-testid={`buyer-${v.id}`} className="p-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
                <div>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="text-base font-semibold">{v.name}</div>
                    {v.category && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[hsl(var(--brass)/0.15)] text-[hsl(var(--brass-2))]">{v.category}</span>
                    )}
                    {!v.active && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]">Inactive</span>}
                  </div>
                  <div className="mt-1 text-sm text-[hsl(var(--ink-soft))]">
                    {v.contact_person && <span>Attn: {v.contact_person}</span>}
                    {v.phone && <> · {v.phone}</>}
                    {v.email && <> · {v.email}</>}
                    {v.gstin && <> · GSTIN: <span className="font-mono">{v.gstin}</span></>}
                  </div>
                  {v.address && <div className="mt-0.5 text-xs text-[hsl(var(--ink-soft))]">{v.address}</div>}
                  {v.payment_terms && <div className="mt-0.5 text-xs text-[hsl(var(--brass-2))]">{v.payment_terms}</div>}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button data-testid={`buyer-ledger-${v.id}`} onClick={() => onViewLedger?.(v.id)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors">
                    <BookOpen size={12} /> Ledger
                  </button>
                  <button data-testid={`buyer-edit-${v.id}`} onClick={() => openEdit(v)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg))]">
                    <Pencil size={12} /> Edit
                  </button>
                  {v.active && (
                    <button data-testid={`buyer-del-${v.id}`} onClick={() => remove(v)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
                      <Trash2 size={12} /> Deactivate
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <div data-testid="buyer-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
          <form onSubmit={save} className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-xl p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="eyebrow">{editing === "new" ? "New buyer" : "Edit buyer"}</div>
                <h2 className="mt-1 text-2xl">{form.name || "—"}</h2>
              </div>
              <button type="button" onClick={close} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Name *
                <input required data-testid="buyer-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="tbb-inp mt-1 w-full" autoFocus />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Category
                <select data-testid="buyer-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="tbb-inp mt-1 w-full">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Contact person
                <input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Phone
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="tbb-inp mt-1 w-full" placeholder="+91 …" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Email
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                GSTIN
                <input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="tbb-inp mt-1 w-full font-mono" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Payment terms
                <input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="tbb-inp mt-1 w-full" placeholder="On delivery / Net 7 / etc." />
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Address
                <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="tbb-inp mt-1 w-full resize-none" />
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Notes
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="tbb-inp mt-1 w-full resize-none" />
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={close} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
              <button type="submit" data-testid="buyer-save" disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">{saving ? "Saving..." : "Save buyer"}</button>
            </div>
            <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
          </form>
        </div>
      )}
    </div>
  );
}
